import {
  Car,
  CheckCircle,
  Clock,
  DollarSign,
  Mail,
  MapPin,
  Phone,
  Shield,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
// Add react-toastify imports
import { toast, ToastContainer } from "react-toastify";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { DASHBOARD_URL } from "@/config/env";
import { submitDriverApplication } from "@/services/apiService";
import { CitiesService } from "@/services/landing/citiesService";
import "react-toastify/dist/ReactToastify.css";

export default function BecomeDriver() {
  const [formData, setFormData] = useState({
    agreeToTerms: false,
    city: "",
    email: "",
    firstName: "",
    hasLicense: false,
    lastName: "",
    phone: "",
    vehicleType: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(true);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setCitiesLoading(true);
        const data = await CitiesService.getAll();
        // Filter active cities and extract names
        const activeCityNames = data
          .filter((city) => city.status === "active" && !city.hidden)
          .map((city) => city.name)
          .sort();
        setCities(activeCityNames);
      } catch (error) {
        console.error("Failed to load cities:", error);
        // Fallback to empty array or show error
        setCities([]);
        toast.error("Failed to load cities. Please try again.");
      } finally {
        setCitiesLoading(false);
      }
    };

    fetchCities();
  }, []);

  const goToDashboard = (role: string) => {
    window.location.href = `${DASHBOARD_URL}/auth/login?role=${role}`;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, type, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      agreed_terms: formData.agreeToTerms,
      city: formData.city,
      email: formData.email,
      first_name: formData.firstName,
      has_license: formData.hasLicense,
      last_name: formData.lastName,
      phone_number: formData.phone,
      vehicle_type: formData.vehicleType,
    };

    try {
      const result = await submitDriverApplication(payload);
      if (result.success) {
        toast.success("Application submitted successfully!");
        setIsSubmitted(true);
      } else {
        const errorMsg =
          result.message ||
          result.error ||
          (result.errors && typeof result.errors === "string"
            ? result.errors
            : Array.isArray(result.errors)
              ? result.errors.join(", ")
              : "Submission failed. Please try again.");
        toast.error(errorMsg);
      }
    } catch (err) {
      if (
        err &&
        typeof err === "object" &&
        "message" in err &&
        typeof (err as { message?: unknown }).message === "string"
      ) {
        toast.error((err as { message: string }).message);
      } else {
        toast.error("Network error. Please try again.");
      }
    }
    setIsSubmitting(false);
  };

  const benefits = [
    {
      description: "Flexible earning potential with weekly payouts",
      icon: <DollarSign className="text-success" size={32} />,
      title: "Earn R4,500+/week",
    },
    {
      description: "Drive when you want, where you want",
      icon: <Clock className="text-primary" size={32} />,
      title: "Flexible Schedule",
    },
    {
      description: "Comprehensive coverage while driving",
      icon: <Shield className="text-secondary" size={32} />,
      title: "Insurance Coverage",
    },
    {
      description: "Round-the-clock driver assistance",
      icon: <Users className="text-accent" size={32} />,
      title: "24/7 Support",
    },
  ];

  const requirements = [
    "Valid driver's license",
    "Vehicle registration documents",
    "Professional driving permit (PrDP)",
    "Clean criminal background check",
    "Vehicle inspection certificate",
    "Smartphone for app usage",
  ];

  if (isSubmitted) {
    return (
      <>
        <AppNavbar />
        <div className="min-h-screen bg-base-200 pt-16">
          <Breadcrumbs />
          <div className="flex items-center justify-center px-4 py-16">
            <div className="max-w-md text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success/20">
                <CheckCircle className="text-success" size={40} />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-success">
                Application Submitted!
              </h1>
              <p className="mb-6 text-base-content/70">
                Thank you for your interest in becoming a Hareeng driver. You
                can check your email for further instructions then login and
                complete application then will Review and get back to you as
                soon as you uploaded required documents.
              </p>
              <div className="space-y-3">
                <div className="alert alert-info">
                  <Mail size={20} />
                  <span>Check your email for next steps</span>
                </div>
                <div className="alert alert-warning">
                  <Phone size={20} />
                  <span>Keep your phone ready for our call</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer autoClose={5000} position="top-right" />
      </>
    );
  }

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="relative overflow-hidden">
          {/* Background Pattern */}
          <div className="pointer-events-none absolute inset-0 select-none opacity-10">
            <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full"></div>
            <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full"></div>
          </div>

          <div className="relative mx-auto w-full max-w-7xl px-4 py-8 sm:py-12 lg:py-24">
            <div className="grid w-full items-center gap-8 lg:grid-cols-2">
              {/* Left Content */}
              <div className="order-2 min-w-0 lg:order-1">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium sm:mb-6 sm:px-4 sm:py-2 sm:text-sm">
                  <Car className="h-4 w-4 sm:h-4 sm:w-4" />
                  <span>Join Hareeng Drivers</span>
                </div>

                <h1 className="mb-4 text-3xl font-bold leading-tight sm:mb-6 sm:text-4xl md:text-5xl lg:text-6xl">
                  Earn up to <span className="text-blue-500">R6,500</span> per
                  week by <span className="text-blue-500">driving</span> with
                  Hareeng
                </h1>

                <p className="mb-6 text-lg opacity-90 sm:mb-8 sm:text-xl lg:text-2xl">
                  Become a Hareeng driver, set your schedule and earn money by
                  driving!
                </p>

                {/* Benefits Grid */}
                <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                  {benefits.map((benefit, index) => (
                    <div
                      className="flex items-start gap-3 rounded-lg bg-white/10 p-3 backdrop-blur sm:items-center sm:p-4"
                      key={index}
                    >
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-white/20 sm:h-12 sm:w-12">
                        {benefit.icon}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold sm:text-base">
                          {benefit.title}
                        </h3>
                        <p className="text-xs opacity-80 sm:text-sm">
                          {benefit.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Form */}
              <div className="order-1 mx-auto w-full min-w-0 max-w-md lg:order-2">
                <div className="card bg-base-100 shadow-xl">
                  <div className="card-body p-4 sm:p-6 md:p-8">
                    <h2 className="card-title mb-4 text-xl sm:mb-6 sm:text-2xl">
                      Become taxi driver
                    </h2>

                    <form
                      className="space-y-3 sm:space-y-4"
                      onSubmit={handleSubmit}
                    >
                      {/* Name Fields */}
                      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                        <div className="form-control">
                          <label className="label pb-1">
                            <span className="label-text text-sm font-medium sm:text-base">
                              First Name
                            </span>
                          </label>
                          <input
                            className="input input-sm input-bordered w-full sm:input-md"
                            name="firstName"
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                            required
                            type="text"
                            value={formData.firstName}
                          />
                        </div>
                        <div className="form-control">
                          <label className="label pb-1">
                            <span className="label-text text-sm font-medium sm:text-base">
                              Last Name
                            </span>
                          </label>
                          <input
                            className="input input-sm input-bordered w-full sm:input-md"
                            name="lastName"
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                            required
                            type="text"
                            value={formData.lastName}
                          />
                        </div>
                      </div>

                      {/* Email Field */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Email
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="email"
                          onChange={handleInputChange}
                          placeholder="Enter email address"
                          required
                          type="email"
                          value={formData.email}
                        />
                      </div>

                      {/* Phone Field */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Phone number
                          </span>
                        </label>
                        <div className="flex w-full flex-col gap-2 sm:flex-row">
                          <select
                            className="select select-bordered select-sm w-full sm:select-md sm:w-28"
                            name="countryCode"
                          >
                            <option>+27</option>
                            <option>+266</option>
                          </select>
                          <input
                            className="input input-sm input-bordered w-full sm:input-md"
                            name="phone"
                            onChange={handleInputChange}
                            placeholder="Mobile number"
                            required
                            type="tel"
                            value={formData.phone}
                          />
                        </div>
                      </div>

                      {/* City Field */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            City
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          disabled={citiesLoading}
                          name="city"
                          onChange={handleInputChange}
                          required
                          value={formData.city}
                        >
                          <option value="">
                            {citiesLoading
                              ? "Loading cities..."
                              : "Select your city"}
                          </option>
                          {cities.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Vehicle Type */}
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Vehicle Type
                          </span>
                        </label>
                        <select
                          className="select select-bordered select-sm w-full sm:select-md"
                          name="vehicleType"
                          onChange={handleInputChange}
                          required
                          value={formData.vehicleType}
                        >
                        <option value="">Select vehicle type</option>
                        <option value="sedan">5 Seater</option>
                        <option value="suv">7 Seater</option>
                        </select>
                      </div>

                      {/* Checkboxes */}
                      <div className="space-y-2 sm:space-y-3">
                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-2 py-2 sm:gap-3">
                            <input
                              checked={formData.hasLicense}
                              className="checkbox-primary checkbox checkbox-sm sm:checkbox-md"
                              name="hasLicense"
                              onChange={handleInputChange}
                              required
                              type="checkbox"
                            />
                            <span className="label-text text-xs sm:text-sm">
                              I have a valid drivers license and PrDP
                            </span>
                          </label>
                        </div>

                        <div className="form-control">
                          <label className="label cursor-pointer justify-start gap-2 py-2 sm:gap-3">
                            <input
                              checked={formData.agreeToTerms}
                              className="checkbox-primary checkbox checkbox-sm sm:checkbox-md"
                              name="agreeToTerms"
                              onChange={handleInputChange}
                              required
                              type="checkbox"
                            />
                            <span className="label-text text-xs leading-relaxed sm:text-sm">
                              By registering, you agree to our{" "}
                              <a
                                className="link link-primary"
                                href="/pages/legal/terms-of-use"
                              >
                                Terms of Service
                              </a>{" "}
                              and{" "}
                              <a
                                className="link link-primary"
                                href="/pages/legal/privacy-policy"
                              >
                                Privacy Policy
                              </a>
                            </span>
                          </label>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        className="btn btn-primary btn-sm w-full text-base sm:btn-md sm:text-lg"
                        disabled={isSubmitting}
                        type="submit"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="loading loading-spinner loading-sm"></span>
                            Processing...
                          </>
                        ) : (
                          "Register as a driver"
                        )}
                      </button>
                    </form>

                    <div className="divider my-2 text-xs sm:my-4 sm:text-sm">
                      OR
                    </div>

                    <p className="text-center text-xs sm:text-sm">
                      Already have an account?{" "}
                      <a
                        className="link link-primary font-medium"
                        href="#"
                        onClick={() => goToDashboard("driver")}
                      >
                        Log in →
                      </a>
                    </p>

                    <p className="text-center text-xs leading-relaxed sm:text-sm">
                      If you have multiple vehicles and drivers,{" "}
                      <a
                        className="link link-secondary font-medium"
                        href="/partners/fleet-registration"
                      >
                        register as a Fleet Owner →
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Requirements Section */}
        <section className="bg-base-100 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="mb-3 text-2xl font-bold text-primary sm:mb-4 sm:text-3xl">
                Driver Requirements
              </h2>
              <p className="mx-auto max-w-2xl text-base text-base-content/70 sm:text-lg">
                Make sure you meet these requirements before applying
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
              {requirements.map((requirement, index) => (
                <div
                  className="flex items-start gap-2 rounded-lg bg-base-200 p-3 sm:gap-3 sm:p-4"
                  key={index}
                >
                  <CheckCircle
                    className="mt-0.5 flex-shrink-0 text-success sm:mt-1"
                    size={16}
                  />
                  <span className="text-sm text-base-content/80 sm:text-base">
                    {requirement}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="mb-3 text-2xl font-bold text-primary sm:mb-4 sm:text-3xl">
                How it Works
              </h2>
              <p className="mx-auto max-w-2xl text-base text-base-content/70 sm:text-lg">
                Start earning in just a few simple steps
              </p>
            </div>

            <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-lg font-bold text-white sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  1
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Apply Online
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  Fill out the application form with your details and documents
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary text-lg font-bold text-white sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  2
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Get Approved
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  We shall review your application and verify your documents
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-lg font-bold text-white sm:mb-6 sm:h-16 sm:w-16 sm:text-xl">
                  3
                </div>
                <h3 className="mb-3 text-lg font-semibold sm:mb-4 sm:text-xl">
                  Start Earning
                </h3>
                <p className="text-sm text-base-content/70 sm:text-base">
                  Download the driver app and start accepting delivery requests
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-3xl md:text-4xl">
              Ready to Start Earning?
            </h2>
            <p className="mb-6 text-lg opacity-90 sm:mb-8 sm:text-xl">
              Join thousands of drivers already earning with Hareeng
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
              <button
                className="btn btn-secondary btn-sm sm:btn-md lg:btn-lg"
                onClick={() => window.scrollTo({ behavior: "smooth", top: 0 })}
              >
                Apply Now
              </button>
              <a
                className="btn btn-outline btn-sm sm:btn-md lg:btn-lg"
                href="/safety/rider-safety"
              >
                Learn More
              </a>
            </div>
          </div>
        </section>
      </div>
      {/* Toast notifications container */}
      <ToastContainer autoClose={5000} position="top-right" />
    </>
  );
}
